import { useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FileIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { CTAButton } from "../CTAButton"
import { ZoomableImage } from "../ZoomableImage"

import * as Styled from "./FileViewer.styled.js"

const unsupportedImageTypes = [
    "image/tiff",
    "image/tiff-fx",
]

export const FileViewer = (props) => {
    const {
        className,
        file,
        onFileChange,
        allowZoom,
        variant,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    const fileInfo = useMemo(() => {
        if (!file) {
            return null
        }
        if (file instanceof File) {
            return {
                name: file.name,
                extension: file.name.split(".").length >= 2 ? file.name.split(".").pop() : "",
                type: file.type,
                url: URL.createObjectURL(file),
                isPDF: file.type === "application/pdf",
                isImage: file.type.startsWith("image/") && !unsupportedImageTypes.includes(file.type),
            }
        } else {
            return {
                name: file.filename,
                extension: file.filename.split(".").length >= 2 ? file.filename.split(".").pop() : "",
                type: file.mime_type,
                url: file.file,
                isPDF: file.mime_type === "application/pdf",
                isImage: file.mime_type.startsWith("image/") && !unsupportedImageTypes.includes(file.mime_type),
            }
        }
    }, [file])

    return (
        <Styled.Container className={className}>
            <Styled.ImageWrapper>
                {fileInfo?.isPDF && <iframe style={{ width: "100%", height: "100%" }} src={fileInfo?.url} title={fileInfo?.name} />}
                {fileInfo?.isImage && <ZoomableImage src={fileInfo?.url} filename={fileInfo?.name} disabled={!allowZoom} />}
                {fileInfo && !fileInfo.isPDF && !fileInfo.isImage && (
                    <Styled.FileIconWrapper >
                        <FileIcon extension={fileInfo.extension} variant={variant} />
                        {variant === "large" && <Styled.NoPreviewMessage variant="Mobile/Body/Bold">{t(I18N_NAMESPACE, "components.fileViewer.noPreview")}</Styled.NoPreviewMessage>}
                    </Styled.FileIconWrapper>
                )}
            </Styled.ImageWrapper>
            {file && onFileChange && <CTAButton
                onClick={onFileChange}
                color="secondary"
                variant="contained">
                <Styled.ChangeIcon />
                {t(I18N_NAMESPACE, "components.fileViewer.change")}
            </CTAButton>}
        </Styled.Container>
    )
}

FileViewer.propTypes = {
    className: PropTypes.string,
    file: PropTypes.oneOfType([
        PropTypes.instanceOf(File),
        PropTypes.shape({
            filename: PropTypes.string.isRequired,
            mime_type: PropTypes.string.isRequired,
            file: PropTypes.string.isRequired,
        }),
    ]).isRequired,
    onFileChange: PropTypes.func,
    allowZoom: PropTypes.bool,
    variant: PropTypes.oneOf(["normal", "large"]),
}

FileViewer.defaultProps = {
    allowZoom: false,
    variant: "normal",
}